import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import dayjs from 'dayjs';
import {
  AutocompleteInput,
  ReferenceInput,
  SelectInput,
  TextInput,
  useDataProvider,
  useListContext,
  useNotify,
  useRefresh,
  useTranslate,
} from 'react-admin';
import { Permission, ReportList } from '../../components';
import { BetweenDatesInput } from '../../components/BetweenDatesInput';
import ListPagination from '../../components/ListPagination';
import { PERMISSIONS } from '../../constants';
import { filterToQuery } from '../../utils';
import { __t } from '../../i18n/t';

/**
 * Relatório "Acessos conectados" — exclusivo do admin PontuaX.
 *
 * Uma linha por sessão de dispositivo do admin. Revogar aqui é a única forma de cassar
 * um acesso sem trocar a senha de alguém: a sessão morre na próxima renovação do access
 * token, no máximo 30 minutos depois.
 *
 * Os sinais de cada linha existem para a decisão de revogar:
 * - **ociosa** há muitos dias: sessão esquecida em máquina que ninguém usa mais;
 * - **IP mudou** desde o cadastro: comum em quem troca de rede, suspeito em quem não;
 * - **nº de dispositivos** do funcionário: 6 sessões ativas de um operador de posto
 *   não combina com uma pessoa só.
 */

interface SessionRow {
  id: string;
  employeeId: string;
  employeeName: string | null;
  employeeLogin: string;
  employeeType: string;
  chainId: string | null;
  chainName: string;
  deviceName: string | null;
  createdIp: string | null;
  lastIp: string | null;
  ipChanged: boolean;
  createdAt: string;
  lastUsedAt: string;
  expiresAt: string;
  revokedAt: string | null;
  revokedReason: string | null;
  status: 'active' | 'revoked' | 'expired';
  idleDays: number;
  employeeActiveSessions: number;
}

const STATUS_CHOICES = [
  { id: 'active', name: 'Ativas' },
  { id: 'revoked', name: 'Revogadas' },
  { id: 'expired', name: 'Expiradas' },
];

const REVOKED_REASONS = {
  user_logout: 'Saiu pelo próprio painel',
  admin_revoked: 'Revogada por um admin',
  admin_revoked_all: 'Revogada em massa por um admin',
  password_changed: 'Troca de senha',
  max_sessions: 'Excedeu o limite de dispositivos',
  replaced_by_login: 'Substituída por um login novo no mesmo navegador',
};

/** Sessão ociosa a partir daqui já merece atenção na hora de limpar acessos. */
const IDLE_WARNING_DAYS = 7;
const MANY_DEVICES = 4;

const browserOf = (deviceName: string | null) => {
  const agent = deviceName || '';
  if (/Edg\//.test(agent)) { return 'Edge'; }
  if (/Chrome\//.test(agent)) { return 'Chrome'; }
  if (/Firefox\//.test(agent)) { return 'Firefox'; }
  if (/Safari\//.test(agent)) { return 'Safari'; }
  return 'Desconhecido';
};

const platformOf = (deviceName: string | null) => {
  const agent = deviceName || '';
  if (/Windows/.test(agent)) { return 'Windows'; }
  if (/Macintosh|Mac OS/.test(agent)) { return 'macOS'; }
  if (/Android/.test(agent)) { return 'Android'; }
  if (/iPhone|iPad/.test(agent)) { return 'iOS'; }
  if (/Linux/.test(agent)) { return 'Linux'; }
  return '';
};

const SummaryTile = ({ label, value, tone }: { label: string, value: number, tone?: 'warning' }) => (
  <Card
    sx={{
      padding: 2,
      minWidth: 150,
      flex: '1 1 150px',
      backgroundColor: tone === 'warning' && value > 0 ? '#FFF4E5' : undefined,
    }}
  >
    <Typography sx={{ fontSize: 24, fontWeight: 600, color: tone === 'warning' && value > 0 ? '#8A4B00' : undefined }}>
      {value}
    </Typography>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
  </Card>
);

const EmployeeSessions = () => {
  const { data, filterValues } = useListContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();

  const report = data?.[0];
  const summary = report?.summaryData || {};
  const sessions: SessionRow[] = report?.data || [];

  const [pending, setPending] = useState<{ row: SessionRow, all: boolean } | null>(null);
  const [working, setWorking] = useState(false);

  const confirmRevoke = async () => {
    if (!pending) { return; }
    setWorking(true);
    try {
      if (pending.all) {
        await dataProvider.create('employees/sessions/revoke-all', { data: { employeeId: pending.row.employeeId } });
        notify('Todos os acessos do funcionário foram revogados', { type: 'info' });
      } else {
        await dataProvider.delete('employees/session', { id: pending.row.id });
        notify('Acesso revogado', { type: 'info' });
      }
      setPending(null);
      refresh();
    } catch (error) {
      notify('Não foi possível revogar o acesso', { type: 'error' });
    } finally {
      setWorking(false);
    }
  };

  const showingActive = (filterValues?.status ?? 'active') === 'active';
  const pendingLabel = pending?.row.employeeName || pending?.row.employeeLogin || '';

  return (
    <Box sx={{ padding: 2 }}>
      <Typography sx={{ marginBottom: 2, padding: '8px 12px', borderRadius: 2, fontSize: 13, backgroundColor: '#E8F4FD', color: '#0B4A6F' }}>
        Revogar encerra o acesso daquele navegador na próxima renovação do token — no
        máximo 30 minutos. Para cortar na hora em caso de máquina comprometida, revogue
        todos os acessos do funcionário e peça a troca da senha.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', marginBottom: 2 }}>
        <SummaryTile label="Sessões ativas" value={Number(summary.activeSessions ?? 0)} />
        <SummaryTile label="Funcionários com acesso" value={Number(summary.employeesWithActive ?? 0)} />
        <SummaryTile label="Funcionários com mais de um acesso" value={Number(summary.multiDeviceEmployees ?? 0)} />
        <SummaryTile label="Ociosas há mais de 7 dias" value={Number(summary.idleSessions ?? 0)} tone="warning" />
        <SummaryTile label="Com IP diferente do cadastro" value={Number(summary.ipChangedSessions ?? 0)} tone="warning" />
        <SummaryTile label="Revogadas" value={Number(summary.revokedSessions ?? 0)} />
      </Box>

      <TableContainer component={Card}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Funcionário</TableCell>
              <TableCell>Rede</TableCell>
              <TableCell>Dispositivo</TableCell>
              <TableCell>IP atual</TableCell>
              <TableCell>Último acesso</TableCell>
              <TableCell>Conectado desde</TableCell>
              <TableCell>Acessos deste funcionário</TableCell>
              <TableCell>Situação</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sessions.map(session => (
              <TableRow key={session.id} hover>
                <TableCell>
                  <Typography variant="body2">{session.employeeName || '—'}</Typography>
                  <Typography variant="caption" color="text.secondary">{session.employeeLogin}</Typography>
                </TableCell>
                <TableCell>{session.chainName || '—'}</TableCell>
                <TableCell>
                  {browserOf(session.deviceName)}
                  {platformOf(session.deviceName) && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {platformOf(session.deviceName)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {session.lastIp || '—'}
                    {session.ipChanged && (
                      <Tooltip title={`Cadastrada em ${session.createdIp}`}>
                        <WarningAmberIcon sx={{ fontSize: 16, color: '#B8860B' }} />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  {dayjs(session.lastUsedAt).format('DD/MM/YYYY HH:mm')}
                  {session.idleDays >= IDLE_WARNING_DAYS && (
                    <Typography variant="caption" sx={{ display: 'block', color: '#8A4B00' }}>
                      {`ociosa há ${session.idleDays} dias`}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{dayjs(session.createdAt).format('DD/MM/YYYY HH:mm')}</TableCell>
                <TableCell>
                  {session.employeeActiveSessions > 1 ? (
                    <Tooltip title={__t('%{total} acessos ativos deste funcionário agora, contando este. "Revogar todos" encerra os %{total}.', { total: session.employeeActiveSessions })}>
                      <Chip
                        size="small"
                        label={__t('%{total} ativos', { total: session.employeeActiveSessions })}
                        color={session.employeeActiveSessions >= MANY_DEVICES ? 'warning' : 'default'}
                      />
                    </Tooltip>
                  ) : (
                    <Typography variant="caption" color="text.secondary">só este</Typography>
                  )}
                </TableCell>
                <TableCell>
                  {session.status === 'active' && <Chip size="small" color="success" label="Ativa" />}
                  {session.status === 'expired' && <Chip size="small" label="Expirada" />}
                  {session.status === 'revoked' && (
                    <Tooltip title={translate(REVOKED_REASONS[session.revokedReason ?? ''] || 'Revogada', { _: 'Revogada' })}>
                      <Chip size="small" color="error" label="Revogada" />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell align="right">
                  {session.status === 'active' && (
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<LogoutIcon />}
                        onClick={() => setPending({ row: session, all: false })}
                      >
                        Revogar
                      </Button>
                      {session.employeeActiveSessions > 1 && (
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          onClick={() => setPending({ row: session, all: true })}
                        >
                          Revogar todos
                        </Button>
                      )}
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {sessions.length === 0 && (
              <TableRow>
                <TableCell colSpan={9}>
                  <Typography variant="body2" color="text.secondary">
                    {showingActive
                      ? 'Nenhum acesso ativo com esses filtros.'
                      : 'Nenhuma sessão encontrada com esses filtros.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!pending} onClose={() => !working && setPending(null)}>
        <DialogTitle>Revogar acesso</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pending?.all
              ? __t('Todos os acessos de %{quem} serão encerrados e a pessoa precisará entrar de novo em cada dispositivo.', { quem: pendingLabel })
              : __t('O acesso deste dispositivo de %{quem} será encerrado.', { quem: pendingLabel })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)} disabled={working}>Cancelar</Button>
          <Button onClick={confirmRevoke} color="error" variant="contained" disabled={working}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const ReportEmployeeSessions = (props: any) => {
  const translate = useTranslate();

  const listFilters = [
    <SelectInput
      key="status"
      source="status"
      label="Situação"
      choices={STATUS_CHOICES}
      emptyText="Ativas"
      alwaysOn
      sx={{ minWidth: 160 }}
    />,
    <TextInput key="search" source="search" label="Nome, login ou IP" alwaysOn sx={{ minWidth: 220 }} />,
    <ReferenceInput key="chainId" source="chainId" reference="chains" sort={{ field: 'name', order: 'ASC' }} alwaysOn>
      <AutocompleteInput
        filterToQuery={filterToQuery}
        emptyText="Todas"
        label="Rede"
        optionText="name"
        sx={{ minWidth: 200 }}
        noOptionsText={translate('ra.input.autocomplete.no_options_text')}
      />
    </ReferenceInput>,
    <BetweenDatesInput key="dates" source="lastUsedAt" label="Último acesso" alwaysOn={false} />,
  ];

  return (
    <Permission permission={PERMISSIONS.ADMIN}>
      <ReportList
        {...props}
        basePath="report-employee-sessions"
        title="Acessos conectados"
        resource="reports/employee-sessions"
        filters={listFilters}
        // Sem período por padrão: sessão aberta há meses e usada ontem é justamente a
        // que interessa, e um range obrigatório a esconderia.
        filterDefaultValues={{ status: 'active' }}
        perPage={25}
        storeKey={false as unknown as string}
        pagination={<ListPagination />}
        bulkActionButtons={false}
        exporter={false}
        titleOnPrint={__t('Relatório de acessos conectados')}
      >
        <EmployeeSessions />
      </ReportList>
    </Permission>
  );
};

export default ReportEmployeeSessions;
